import { useEffect, useRef, useState } from "react";
import type { Phase } from "../../shared/types";
import type { MusicTheme } from "./tracks";

interface Bg {
  url?: string;
  loop: boolean;
  vol: number;
}

/** Map a game phase to the background track + target volume. reveal/leaderboard
    return to the (softer) lobby bed; podium plays its own track once. */
function bgFor(phase: Phase | undefined, theme: MusicTheme): Bg {
  switch (phase) {
    case "lobby":
      return { url: theme.lobby, loop: true, vol: 0.5 };
    case "question":
      return { url: theme.question, loop: true, vol: 0.6 };
    case "reveal":
    case "leaderboard":
      return { url: theme.lobby, loop: true, vol: 0.4 };
    case "podium":
      return { url: theme.podium, loop: false, vol: 0.7 };
    default:
      return { url: undefined, loop: false, vol: 0 };
  }
}

/** Linear volume ramp; calls done() when it reaches `to`. */
function fade(a: HTMLAudioElement, to: number, ms: number, done?: () => void) {
  const from = a.volume;
  const start = performance.now();
  const tick = () => {
    const k = Math.min(1, (performance.now() - start) / ms);
    a.volume = Math.max(0, Math.min(1, from + (to - from) * k));
    if (k < 1) requestAnimationFrame(tick);
    else done?.();
  };
  requestAnimationFrame(tick);
}

export interface GameAudio {
  muted: boolean;
  blocked: boolean;
  toggleMute: () => void;
  retry: () => void;
}

/** Host-only background music driven by the live phase. No-op for theme "none".
    `mutedOverride`, when defined, makes mute a controlled prop — used in remote
    mode so the host's phone (via `snapshot.musicMuted`) drives the big screen. */
export function useGameAudio({
  phase,
  theme,
  mutedOverride,
}: {
  phase: Phase | undefined;
  theme: MusicTheme;
  mutedOverride?: boolean;
}): GameAudio {
  const [internalMuted, setMuted] = useState(false);
  const muted = mutedOverride ?? internalMuted;
  const [blocked, setBlocked] = useState(false);
  const elRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | undefined>(undefined);
  const volRef = useRef(0);
  const mutedRef = useRef(false);
  const enabled = theme.id !== "none";

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  // Drive playback from the phase.
  useEffect(() => {
    if (!enabled) return;
    const { url, loop, vol } = bgFor(phase, theme);
    volRef.current = vol;
    const el = elRef.current ?? (elRef.current = new Audio());
    el.loop = loop;

    if (url === urlRef.current) {
      // Same track persists across phases — just glide to the new target volume.
      if (url && !mutedRef.current) fade(el, vol, 400);
      return;
    }
    urlRef.current = url;
    if (!url) {
      fade(el, 0, 350, () => el.pause());
      return;
    }
    const startPlay = () => {
      el.src = url;
      el.volume = 0;
      el.play()
        .then(() => {
          setBlocked(false);
          if (!mutedRef.current) fade(el, vol, 400);
        })
        .catch(() => setBlocked(true));
    };
    if (!el.paused && el.currentSrc) fade(el, 0, 250, startPlay);
    else startPlay();
  }, [phase, enabled, theme]);

  // Mute toggle.
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    if (muted) {
      fade(el, 0, 200, () => el.pause());
    } else if (urlRef.current) {
      el.play()
        .then(() => {
          setBlocked(false);
          fade(el, volRef.current, 300);
        })
        .catch(() => setBlocked(true));
    }
  }, [muted]);

  // Stop and release on unmount (host left the game).
  useEffect(
    () => () => {
      elRef.current?.pause();
      elRef.current = null;
    },
    [],
  );

  const retry = () => {
    const el = elRef.current;
    if (!el || !urlRef.current) return;
    el.play()
      .then(() => {
        setBlocked(false);
        fade(el, volRef.current, 300);
      })
      .catch(() => setBlocked(true));
  };

  return { muted, blocked, toggleMute: () => setMuted((m) => !m), retry };
}
